/**
 * perf-budget.ts — device-tier helpers.
 *
 * THREE TIERS — detected at module load and re-checked on battery change:
 *
 *   "high"  — desktop, fine pointer, plugged-in              → full fidelity
 *   "mid"   — desktop on battery, OR phone plugged-in        → DPR 1.0, 30 FPS, 60% particles, all layers
 *   "low"   — phone on battery, OR any saveData, OR reduced-motion preference,
 *             OR <4 cores, OR <4 GB RAM, OR 2g connection    → DPR 0.75, 20 FPS, 35% particles, SINGLE bg canvas
 *
 * REVISION: phones are now mid-MINIMUM (was can-be-high), and any phone on
 * battery is low-tier. Tested on Brave/Android: previous "mid" was still
 * jank-prone on integrated mobile GPUs because all 4 bg canvases + screen-
 * blend compositing exceeded the per-frame budget. Low-tier culls 3 of 4
 * canvases, all mix-blend-mode, and drop-shadow blurs > 30px.
 *
 * Exposes `data-perf-tier="low|mid|high"` on <html> so CSS can specialise.
 */

export type DeviceTier = "low" | "mid" | "high";

type NavigatorWithExtras = Navigator & {
  readonly hardwareConcurrency?: number;
  readonly deviceMemory?: number;
  readonly connection?: {
    readonly effectiveType?: string;
    readonly saveData?: boolean;
  };
  getBattery?: () => Promise<{ readonly charging: boolean; addEventListener?: (e: string, cb: () => void) => void }>;
};

const isPhone = (): boolean => {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  const coarse = window.matchMedia("(pointer: coarse)").matches;
  const narrow = window.matchMedia("(max-width: 768px)").matches;
  return coarse || narrow;
};

const prefersReducedMotion = (): boolean => {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
};

const detectStaticTier = (): DeviceTier => {
  if (typeof window === "undefined") return "high";

  const phone = isPhone();
  const nav = navigator as NavigatorWithExtras;
  const cores = nav.hardwareConcurrency ?? 8;
  const mem = nav.deviceMemory ?? 8;
  const conn = nav.connection;
  const saveData = conn?.saveData === true;
  const slowConn = conn?.effectiveType === "2g" || conn?.effectiveType === "slow-2g";

  // Hard low-tier triggers — any one drops the device.
  if (prefersReducedMotion() || saveData || slowConn || cores < 4 || mem < 4) return "low";
  // Phones default to mid (was "can-be-high if hardware allowed").
  if (phone) return "mid";
  return "high";
};

let TIER: DeviceTier = detectStaticTier();

const applyTierAttr = (): void => {
  if (typeof document !== "undefined" && document.documentElement) {
    document.documentElement.setAttribute("data-perf-tier", TIER);
  }
};
applyTierAttr();

// Battery check — phones unplugged drop one tier. Re-fires on plug-state change.
const watchBattery = async (): Promise<void> => {
  if (typeof navigator === "undefined") return;
  const nav = navigator as NavigatorWithExtras;
  const getBattery = nav.getBattery;
  if (typeof getBattery !== "function") return;
  try {
    const battery = await getBattery.call(nav);
    const downgradeIfUnplugged = (): void => {
      const base = detectStaticTier();
      if (battery.charging) {
        TIER = base;
      } else {
        // Unplugged: one tier down. high→mid, mid→low, low stays low.
        TIER = base === "high" ? "mid" : base === "mid" ? "low" : "low";
      }
      applyTierAttr();
    };
    downgradeIfUnplugged();
    battery.addEventListener?.("chargingchange", downgradeIfUnplugged);
  } catch {
    /* getBattery rejected — keep static tier */
  }
};
void watchBattery();

export const getDeviceTier = (): DeviceTier => TIER;

/** Canvas backing-store ratio. Low-tier sub-native for ~56% fill-rate cut. */
export const getCanvasDpr = (): number => {
  const native = window.devicePixelRatio || 1;
  const cap = TIER === "high" ? 1.5 : TIER === "mid" ? 1.0 : 0.75;
  return Math.min(native, cap);
};

/** Frame budget in ms — low-tier even primary at 30 FPS. */
export const getFrameBudgetMs = (layer: "primary" | "atmosphere"): number => {
  if (layer === "primary") return TIER === "low" ? 33.33 : 16.67;
  if (TIER === "high") return 16.67;
  if (TIER === "mid") return 33.33;     // 30 FPS atmosphere
  return 50.0;                          // 20 FPS atmosphere on low-tier
};

/** Particle-count multiplier. Second 50% cut on top of the prior 75% cut
 *  per user direction — BG is true atmosphere, almost ambient. */
export const getParticleScale = (): number => {
  if (TIER === "high") return 0.15;     // was 0.30
  if (TIER === "mid")  return 0.09;     // was 0.18
  return 0.05;                          // was 0.10
};

/** Whether a non-essential canvas layer should render at all.
 *  Aggressive culling on phones — even mid-tier drops everything except
 *  constellation (the lightest layer) because Android Chrome/Brave still
 *  struggles with multiple canvas + mix-blend compositing passes. */
export const shouldRender = (
  layer: "ribbons" | "nebula" | "rings" | "constellation" | "starfield",
): boolean => {
  // The player's volumetric starfield is the ONE atmosphere layer on that
  // route (the four bg canvases aren't mounted there), so it survives one
  // tier longer than the rest. It still goes on low-tier — a phone on
  // battery running a 20-minute meditation gets a still room.
  if (layer === "starfield") return TIER !== "low";
  if (TIER === "high") return true;
  // mid + low: ONLY constellation
  return layer === "constellation";
};

/** Audio FFT bin count. Low-tier halves it (128 bins) — still plenty for
 *  rms-style level detection, cuts ~50% of FFT CPU per frame. */
export const getAudioFftSize = (): number => {
  return TIER === "low" ? 128 : 256;
};
